import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { isViewerFromStorage } from '../utils/staffAccess';
import './OrderCustomerInfo.css';

const emptyForm = () => ({
  nombre: '',
  email: '',
  telefono: '',
  domicilio: '',
  localidad: '',
  codigoPostal: '',
  provincia: '',
  pais: '',
  cuit: '',
  informacionAdicional: ''
});

const formFromItem = (item) => ({
  nombre: String(item?.nombre || item?.customerName || ''),
  email: String(item?.email || ''),
  telefono: String(item?.telefono || item?.phone || ''),
  domicilio: String(item?.domicilio || ''),
  localidad: String(item?.localidad || ''),
  codigoPostal: String(item?.codigoPostal || ''),
  provincia: String(item?.provincia || ''),
  pais: String(item?.pais || ''),
  cuit: String(item?.cuit || item?.cuil || ''),
  informacionAdicional: String(item?.informacionAdicional || item?.additionalInfo || '')
});

const displayOrDash = (value) => {
  const text = String(value || '').trim();
  return text || '—';
};

const OrderCustomerInfo = ({ item, collectionName, showMessage, onSaved }) => {
  const canEdit = !isViewerFromStorage();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setForm(formFromItem(item));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(emptyForm());
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    if (!item?.id || !collectionName) return;
    const nombre = form.nombre.trim();
    const email = form.email.trim();
    if (!nombre) {
      showMessage('El nombre no puede quedar vacío.');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showMessage('Ingresá un email válido.');
      return;
    }
    setSaving(true);
    try {
      const cuitVal = form.cuit.trim().slice(0, 15);
      const extra = form.informacionAdicional.trim().slice(0, 500);
      const baseFields = {
        nombre: nombre.slice(0, 80),
        email: email.slice(0, 100),
        telefono: form.telefono.trim().slice(0, 30),
        domicilio: form.domicilio.trim().slice(0, 120),
        localidad: form.localidad.trim().slice(0, 80),
        codigoPostal: form.codigoPostal.trim().slice(0, 12),
        provincia: form.provincia.trim().slice(0, 80),
        pais: form.pais.trim().slice(0, 50),
        cuit: cuitVal || null,
        informacionAdicional: extra
      };
      const stamp = {
        customerInfoUpdatedAt: new Date(),
        customerInfoUpdatedBy: auth.currentUser?.email || ''
      };
      const patch = collectionName === 'ventas_fisicas'
        ? {
            'customerData.name': baseFields.nombre,
            'customerData.email': baseFields.email,
            'customerData.phone': baseFields.telefono,
            'customerData.address': baseFields.domicilio,
            'customerData.city': baseFields.localidad,
            'customerData.postalCode': baseFields.codigoPostal,
            'customerData.province': baseFields.provincia,
            'customerData.country': baseFields.pais,
            'customerData.cuit': cuitVal || '',
            'customerData.additionalInfo': extra,
            ...stamp
          }
        : { ...baseFields, ...stamp };
      await updateDoc(doc(db, collectionName, item.id), patch);
      if (typeof onSaved === 'function') {
        onSaved(baseFields);
      }
      showMessage('Datos del cliente actualizados.');
      setEditing(false);
    } catch (error) {
      console.error('Error actualizando datos del cliente:', error);
      const code = error?.code || '';
      if (code === 'permission-denied') {
        showMessage('No tenés permiso para editar este pedido.');
      } else {
        showMessage('Error al guardar los datos del cliente.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="customer-info">
      <div className="customer-info-header">
        <h4>Información del Cliente</h4>
        {canEdit && !editing && (
          <button
            type="button"
            className="btn btn-outline customer-info-edit-btn"
            onClick={startEdit}
          >
            Editar
          </button>
        )}
      </div>

      {editing ? (
        <>
          <div className="info-grid customer-info-form">
            <label>
              <strong>Nombre</strong>
              <input name="nombre" value={form.nombre} onChange={onChange} maxLength={80} disabled={saving} />
            </label>
            <label>
              <strong>Email</strong>
              <input type="email" name="email" value={form.email} onChange={onChange} maxLength={100} disabled={saving} />
            </label>
            <label>
              <strong>Teléfono</strong>
              <input name="telefono" value={form.telefono} onChange={onChange} maxLength={30} disabled={saving} />
            </label>
            <label>
              <strong>Dirección</strong>
              <input name="domicilio" value={form.domicilio} onChange={onChange} maxLength={120} disabled={saving} />
            </label>
            <label>
              <strong>Ciudad</strong>
              <input name="localidad" value={form.localidad} onChange={onChange} maxLength={80} disabled={saving} />
            </label>
            <label>
              <strong>Código postal</strong>
              <input name="codigoPostal" value={form.codigoPostal} onChange={onChange} maxLength={12} disabled={saving} />
            </label>
            <label>
              <strong>Provincia</strong>
              <input name="provincia" value={form.provincia} onChange={onChange} maxLength={80} disabled={saving} />
            </label>
            <label>
              <strong>País</strong>
              <input name="pais" value={form.pais} onChange={onChange} maxLength={50} disabled={saving} />
            </label>
            <label>
              <strong>CUIT / CUIL / DNI</strong>
              <input name="cuit" value={form.cuit} onChange={onChange} maxLength={15} disabled={saving} />
            </label>
            <label className="customer-info-form-wide">
              <strong>Info adicional</strong>
              <textarea
                name="informacionAdicional"
                value={form.informacionAdicional}
                onChange={onChange}
                maxLength={500}
                rows={3}
                disabled={saving}
              />
            </label>
          </div>
          <div className="customer-info-actions">
            <button type="button" className="btn btn-outline" onClick={cancelEdit} disabled={saving}>
              Cancelar
            </button>
            <button type="button" className="btn btn-success" onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </>
      ) : (
        <div className="info-grid">
          <div><strong>Nombre:</strong> {displayOrDash(item.nombre)}</div>
          <div><strong>Email:</strong> {displayOrDash(item.email)}</div>
          <div><strong>Teléfono:</strong> {displayOrDash(item.telefono)}</div>
          <div><strong>Dirección:</strong> {displayOrDash(item.domicilio)}</div>
          <div>
            <strong>Ciudad:</strong> {displayOrDash(item.localidad)}
            {item.codigoPostal ? ` (CP: ${item.codigoPostal})` : ''}
          </div>
          <div><strong>Provincia:</strong> {displayOrDash(item.provincia)}</div>
          <div><strong>País:</strong> {displayOrDash(item.pais)}</div>
          <div><strong>CUIT / DNI:</strong> {displayOrDash(item.cuit || item.cuil)}</div>
          <div><strong>Info adicional:</strong> {displayOrDash(item.informacionAdicional)}</div>
        </div>
      )}
    </div>
  );
};

export default OrderCustomerInfo;
